import type { SignInStatus, TeamPerson, TeamPersonBody, TeamResponse, TeamRole } from '@pulse/contracts';
import * as React from 'react';

import { PageHeader } from '@/components/pulse/page-header';
import { PeopleTabs } from '@/domains/people/people-page';
import { useChangeTeam, useTeam, type TeamChange } from '@/domains/users/api';
import { ApiRequestError } from '@/lib/api-client';

const inputStyle: React.CSSProperties = { height: 40, padding: '0 12px', border: '1px solid var(--line)', borderRadius: 10, background: 'var(--surface-2)', color: 'var(--ink)', minWidth: 0 };

const STATUS: Readonly<Record<SignInStatus, { readonly label: string; readonly chip: string }>> = {
  'no-email': { label: 'No email', chip: 'c-warn' },
  'not-invited': { label: 'Not invited', chip: 'c-warn' },
  invited: { label: 'Invited', chip: 'c-review' },
  'signed-in': { label: 'Signed in', chip: 'c-merged' },
};

/** People › Team (ACS-001, ACS-002), built from the prototype's `viewTeam()`. Admins only. */
export function TeamPage(): React.ReactElement {
  const page = useTeam();
  const change = useChangeTeam();
  const error = change.error instanceof ApiRequestError ? change.error.toUserMessage() : change.error instanceof Error ? change.error.message : null;
  const refused = page.error instanceof ApiRequestError && page.error.statusCode === 403 ? page.error.toUserMessage() : null;
  return (
    <>
      <PageHeader eyebrow="People · team" title="Team" sub="Who can sign in to Pulse. Only admins see this page." />
      <PeopleTabs active="team" />
      {refused !== null ? <section className="card pad">{refused}</section> : null}
      {page.data !== undefined ? (
        <TeamView
          data={page.data}
          busy={change.isPending}
          error={error}
          onChange={(next, done) => {
            change.reset();
            change.mutate(next, { onSuccess: () => done?.() });
          }}
        />
      ) : null}
    </>
  );
}

export function TeamView({
  data,
  onChange,
  busy,
  error,
}: {
  readonly data: TeamResponse;
  readonly onChange: (change: TeamChange, done?: () => void) => void;
  readonly busy: boolean;
  readonly error: string | null;
}): React.ReactElement {
  const [editing, setEditing] = React.useState<string | null>(null);
  const [confirming, setConfirming] = React.useState<string | null>(null);
  // Which card a failure belongs to: the list (invite, remove) or the Add form.
  const [where, setWhere] = React.useState<'list' | 'add'>('list');
  const act = (change: TeamChange, place: 'list' | 'add', done?: () => void) => {
    setWhere(place);
    onChange(change, done);
  };
  return (
    <div className="two" style={{ gridTemplateColumns: 'minmax(0,1fr) 340px', alignItems: 'start' }}>
      <section className="card pad stack" style={{ gap: 6 }} aria-labelledby="team-people">
        <h2 id="team-people">People</h2>
        {data.people.map((person) =>
          editing === person.userId ? (
            <div key={person.userId} className="prow" style={{ gridTemplateColumns: 'minmax(0,1fr)' }}>
              <PersonForm
                label={`Edit ${person.displayName}`}
                initial={person}
                self={person.self}
                busy={busy}
                error={error}
                actions={(body) => (
                  <>
                    <button type="button" className="btn primary" disabled={busy} onClick={() => act({ kind: 'edit', userId: person.userId, body }, 'list', () => setEditing(null))}>
                      Save
                    </button>
                    <button type="button" className="btn" onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                  </>
                )}
              />
            </div>
          ) : (
            <React.Fragment key={person.userId}>
              <PersonRow
                person={person}
                busy={busy}
                onInvite={() => act({ kind: 'invite', userId: person.userId }, 'list')}
                onEdit={() => {
                  setConfirming(null);
                  setEditing(person.userId);
                }}
                onRemove={() => setConfirming(person.userId)}
              />
              {confirming === person.userId ? (
                <div className="confirm" role="alertdialog" aria-labelledby={`remove-${person.userId}`}>
                  <strong id={`remove-${person.userId}`}>Remove {person.displayName} from the team?</strong>
                  <span>They can no longer sign in. Their past work stays in Pulse, credited to them.</span>
                  <div className="row" style={{ gap: 8 }}>
                    <button type="button" className="btn danger" disabled={busy} onClick={() => act({ kind: 'remove', userId: person.userId }, 'list', () => setConfirming(null))}>
                      Remove and disable sign-in
                    </button>
                    <button type="button" className="btn" onClick={() => setConfirming(null)}>
                      Keep them
                    </button>
                  </div>
                </div>
              ) : null}
            </React.Fragment>
          ),
        )}
        {data.agents.map((agent) => (
          <div key={agent.displayName} className="prow team-row">
            <span className="row" style={{ gap: 10 }}>
              <span className="avatar sm" style={{ background: agent.avatarColour }}>
                {agent.initials}
              </span>
              <span className="stack">
                <span style={{ fontWeight: 600 }}>{agent.displayName}</span>
                {agent.githubLogin !== null ? (
                  <span className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {agent.githubLogin}
                  </span>
                ) : null}
              </span>
            </span>
            <span style={{ color: 'var(--muted)' }}>Never signs in: an agent reports through the Mini.</span>
            <span />
          </div>
        ))}
        {error !== null && editing === null && where === 'list' ? (
          <span role="alert" style={{ fontSize: 13, color: 'var(--bad-ink)', paddingTop: 8 }}>
            {error}
          </span>
        ) : null}
      </section>
      <section className="card pad stack" style={{ gap: 12 }} aria-labelledby="team-add">
        <h2 id="team-add">Add a person</h2>
        {editing === null ? (
          <PersonForm
            label="Add a person"
            initial={null}
            self={false}
            busy={busy}
            error={where === 'add' ? error : null}
            actions={(body, clear) => (
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <button type="button" className="btn primary" disabled={busy} onClick={() => act({ kind: 'add', body: { ...body, invite: true } }, 'add', clear)}>
                  Add and invite
                </button>
                <button type="button" className="btn" disabled={busy} onClick={() => act({ kind: 'add', body: { ...body, invite: false } }, 'add', clear)}>
                  Add without inviting
                </button>
              </div>
            )}
          />
        ) : (
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>Finish editing first.</span>
        )}
        {editing === null ? <span style={{ fontSize: 12, color: 'var(--muted)' }}>Their GitHub and Teamwork work counts from the next collection. People without a GitHub login sign in to read.</span> : null}
      </section>
    </div>
  );
}

function PersonRow({ person, busy, onInvite, onEdit, onRemove }: { readonly person: TeamPerson; readonly busy: boolean; readonly onInvite: () => void; readonly onEdit: () => void; readonly onRemove: () => void }): React.ReactElement {
  const status = STATUS[person.status];
  return (
    <div className="prow team-row">
      <span className="row" style={{ gap: 10, minWidth: 0 }}>
        <span className="avatar sm" style={{ background: person.avatarColour }}>
          {person.initials}
        </span>
        <span className="stack" style={{ minWidth: 0 }}>
          <span className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600 }}>{person.displayName}</span>
            <span className="chip c-kind">{person.role === 'admin' ? 'Admin' : 'Member'}</span>
          </span>
          <span className={person.githubLogin !== null ? 'mono' : undefined} style={{ fontSize: 12, color: 'var(--muted)' }}>
            {person.githubLogin ?? 'No GitHub login'}
          </span>
        </span>
      </span>
      <span className="stack" style={{ gap: 2, minWidth: 0 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...(person.emailAddress === null ? { color: 'var(--muted)' } : {}) }}>{person.emailAddress ?? 'No email yet'}</span>
        <span className="row" style={{ gap: 6 }}>
          <span className={`chip ${status.chip}`}>{status.label}</span>
          {person.sinceLabel !== null ? <span style={{ fontSize: 12, color: 'var(--muted)' }}>{person.sinceLabel}</span> : null}
        </span>
      </span>
      <span className="row team-actions" style={{ gap: 6, flexWrap: 'wrap' }}>
        {person.status === 'signed-in' ? null : (
          <button type="button" className="btn" style={{ height: 32 }} disabled={busy || person.emailAddress === null} title={person.emailAddress === null ? 'Add their email first' : undefined} onClick={onInvite}>
            {person.status === 'invited' ? 'Resend invite' : 'Invite'}
          </button>
        )}
        <button type="button" className="btn" style={{ height: 32 }} onClick={onEdit}>
          Edit
        </button>
        {person.self ? null : (
          <button type="button" className="btn danger" style={{ height: 32 }} onClick={onRemove}>
            Remove
          </button>
        )}
      </span>
    </div>
  );
}

/** Name, email, an optional GitHub login and a role (ACS-001 AC-2, AC-4), as in the prototype's `teamForm()`. */
function PersonForm({
  label,
  initial,
  self,
  busy,
  error,
  actions,
}: {
  readonly label: string;
  readonly initial: TeamPerson | null;
  readonly self: boolean;
  readonly busy: boolean;
  readonly error: string | null;
  readonly actions: (body: TeamPersonBody, clear: () => void) => React.ReactNode;
}): React.ReactElement {
  const [displayName, setDisplayName] = React.useState(initial?.displayName ?? '');
  const [emailAddress, setEmailAddress] = React.useState(initial?.emailAddress ?? '');
  const [githubLogin, setGithubLogin] = React.useState(initial?.githubLogin ?? '');
  const [role, setRole] = React.useState<TeamRole>(initial?.role ?? 'member');
  const id = React.useId();
  const nameRef = React.useRef<HTMLInputElement>(null);
  const editing = initial !== null;
  // Opening Edit puts the cursor in the name, as the prototype does.
  React.useEffect(() => {
    if (editing) nameRef.current?.focus();
  }, [editing]);
  const clear = () => {
    setDisplayName('');
    setEmailAddress('');
    setGithubLogin('');
    setRole('member');
  };
  const body: TeamPersonBody = { displayName: displayName.trim(), emailAddress: emailAddress.trim() === '' ? null : emailAddress.trim(), githubLogin: githubLogin.trim() === '' ? null : githubLogin.trim(), role };
  return (
    <section className="stack" style={{ gap: 12, maxWidth: 520 }} aria-label={label}>
      <label className="stack" style={{ gap: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 500 }}>Name</span>
        <input id={`${id}-name`} value={displayName} onChange={(event) => setDisplayName(event.target.value)} style={inputStyle} ref={nameRef} />
      </label>
      <label className="stack" style={{ gap: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 500 }}>Email</span>
        <input type="email" value={emailAddress} onChange={(event) => setEmailAddress(event.target.value)} placeholder="name@ten24.co" style={inputStyle} />
      </label>
      <label className="stack" style={{ gap: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 500 }}>
          GitHub login <span style={{ fontWeight: 400, color: 'var(--muted)' }}>(optional)</span>
        </span>
        <input className="mono" value={githubLogin} onChange={(event) => setGithubLogin(event.target.value)} style={inputStyle} />
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>Only for people whose GitHub work Pulse should count.</span>
      </label>
      <fieldset className="stack" style={{ gap: 6, border: 0, padding: 0, margin: 0 }}>
        <legend style={{ fontSize: 13, fontWeight: 500, paddingBottom: 4 }}>Role</legend>
        <div className="seg" role="group" aria-label="Role">
          {(['member', 'admin'] as const).map((option) => (
            <button key={option} type="button" aria-pressed={role === option} disabled={self || busy} onClick={() => setRole(option)}>
              {option === 'admin' ? 'Admin' : 'Member'}
            </button>
          ))}
        </div>
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>{self ? "You can't change your own role." : 'Admins also manage the team and scope, and can stop Talos.'}</span>
      </fieldset>
      {error !== null ? (
        <span role="alert" style={{ fontSize: 13, color: 'var(--bad-ink)' }}>
          {error}
        </span>
      ) : null}
      <div className="row" style={{ gap: 8 }}>
        {actions(body, clear)}
      </div>
    </section>
  );
}
